import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Chip,
    CircularProgress,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    Menu,
    MenuItem,
    Paper,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import ExpandLessOutlinedIcon from "@mui/icons-material/ExpandLessOutlined";
import ExpandMoreOutlinedIcon from "@mui/icons-material/ExpandMoreOutlined";
import MoreVertOutlinedIcon from "@mui/icons-material/MoreVertOutlined";
import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";
import ReplayOutlinedIcon from "@mui/icons-material/ReplayOutlined";
import RuleOutlinedIcon from "@mui/icons-material/RuleOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import moment from "moment";
import _ from "lodash";
import Div from "@jumbo/shared/Div";
import CustomPagination from "app/widgets/Shared/Pagination";
import useJumboAuth from "@jumbo/hooks/useJumboAuth";
import { hasProfile } from "app/utils/appHelpers";
import deviceAlertServices from "app/services/automations/device-alert-services";
import { CreateButton, StandardFiltersBar, StandardPageHeader, StandardSearchField } from "app/shared/ui";

const UNGROUPED_GROUP_LABEL = "Sin agrupar";
const UNGROUPED_GROUP_VALUE = "__ungrouped";

const CHANNEL_LABELS = {
    email: "Email",
    whatsapp: "WhatsApp",
    slack: "Slack",
};

// Origen de la notificación: una regla o un aviso de seguridad de un control, un límite o un equipo.
const SOURCE_LABELS = {
    rule: "Regla",
    safety: "Aviso de seguridad",
};

const STATUS_LABELS = {
    pending: "Pendiente",
    sent: "Enviada",
    failed: "Fallida",
    skipped: "Omitida",
};

const DeviceAlertNotificationsIndex = () => {
    const { authUser } = useJumboAuth();
    const isSuperadmin = _.some(authUser?.current_profiles, { name: "superadmin" }) || hasProfile(authUser, "superadmin");

    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [recordsList, setRecordsList] = useState([]);
    const [filterOptions, setFilterOptions] = useState({});
    const [filterOptionsLoading, setFilterOptionsLoading] = useState(false);
    const [searchInput, setSearchInput] = useState("");
    const [searchQuery, setSearchQuery] = useState("");
    const [channelFilter, setChannelFilter] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [sourceFilter, setSourceFilter] = useState("");
    const [automationFilter, setAutomationFilter] = useState("");
    const [deviceFilter, setDeviceFilter] = useState("");
    const [groupFilter, setGroupFilter] = useState("");
    const [fromDateFilter, setFromDateFilter] = useState("");
    const [toDateFilter, setToDateFilter] = useState("");
    const [listLoading, setListLoading] = useState(false);
    const [openTestModal, setOpenTestModal] = useState(false);
    const [testOptions, setTestOptions] = useState(null);
    const [testOptionsLoading, setTestOptionsLoading] = useState(false);
    const [sendingTest, setSendingTest] = useState(false);
    const [lastDispatchMessage, setLastDispatchMessage] = useState("");
    const [selectedTestUsers, setSelectedTestUsers] = useState([]);
    const [selectedTestChannels, setSelectedTestChannels] = useState([]);
    const [selectedTestDevice, setSelectedTestDevice] = useState(null);
    const [testMessage, setTestMessage] = useState("Notificación de prueba enviada manualmente");
    const [testSlackWebhook, setTestSlackWebhook] = useState("");
    const [lastDispatchSeverity, setLastDispatchSeverity] = useState("success");
    const [resendingNotificationId, setResendingNotificationId] = useState(null);
    const [notificationMenuAnchorEl, setNotificationMenuAnchorEl] = useState(null);
    const [notificationMenuRecord, setNotificationMenuRecord] = useState(null);
    const [expandedRecordIds, setExpandedRecordIds] = useState({});

    const requestRecordsReload = useCallback((resetPage = true) => {
        if (resetPage) setCurrentPage(1);
        setListLoading(true);
    }, []);

    const buildRecordParams = useCallback(() => {
        const params = new URLSearchParams();
        params.append("page", currentPage);

        if (channelFilter) params.append("q[channel_eq]", channelFilter);
        if (statusFilter) params.append("q[status_eq]", statusFilter);
        if (sourceFilter) params.append("source", sourceFilter);
        if (automationFilter) params.append("automation", automationFilter);
        if (deviceFilter) params.append("q[device_id_eq]", deviceFilter);
        if (groupFilter) params.append("notification_group", groupFilter);
        if (fromDateFilter) params.append("q[created_at_gteq]", moment(fromDateFilter).startOf("day").toISOString());
        if (toDateFilter) params.append("q[created_at_lteq]", moment(toDateFilter).endOf("day").toISOString());

        return `?${params.toString()}`;
    }, [automationFilter, channelFilter, currentPage, deviceFilter, fromDateFilter, groupFilter, sourceFilter, statusFilter, toDateFilter]);

    const loadRecords = useCallback(() => {
        deviceAlertServices.getNotifications(buildRecordParams())
            .then((resp) => {
                setRecordsList(resp?.data || []);
                setTotalPages(resp?.meta?.total_pages || 1);
                setTotalCount(resp?.meta?.total_count || 0);
            })
            .catch(() => {})
            .finally(() => {
                setListLoading(false);
            });
    }, [buildRecordParams]);

    const loadFilterOptions = useCallback(() => {
        setFilterOptionsLoading(true);
        deviceAlertServices.getNotificationFilterOptions()
            .then((resp) => {
                setFilterOptions(resp || {});
            })
            .catch(() => {})
            .finally(() => {
                setFilterOptionsLoading(false);
            });
    }, []);

    useEffect(() => {
        if (listLoading) {
            loadRecords();
        }
    }, [listLoading, loadRecords]);

    useEffect(() => {
        moment.locale("es-cl");
        loadFilterOptions();
        setListLoading(true);
    }, [loadFilterOptions]);

    const resetTestForm = () => {
        setSelectedTestUsers([]);
        setSelectedTestChannels([]);
        setSelectedTestDevice(null);
        setTestMessage("Notificación de prueba enviada manualmente");
        setTestSlackWebhook("");
    };

    const loadTestOptions = () => {
        if (!isSuperadmin) return;

        setTestOptionsLoading(true);
        deviceAlertServices.getTestOptions()
            .then((resp) => {
                setTestOptions(resp || {});
            })
            .catch(() => {})
            .finally(() => {
                setTestOptionsLoading(false);
            });
    };

    const openTestNotificationsModal = () => {
        setLastDispatchMessage("");
        setLastDispatchSeverity("success");
        resetTestForm();
        setOpenTestModal(true);
        loadTestOptions();
    };

    const closeTestNotificationsModal = () => {
        setOpenTestModal(false);
        setSendingTest(false);
    };

    const sendTestNotifications = () => {
        if (!selectedTestUsers.length || !selectedTestChannels.length) return;

        setSendingTest(true);
        deviceAlertServices.sendTestNotification({
            test_notification: {
                user_ids: _.map(selectedTestUsers, "id"),
                channels: selectedTestChannels,
                device_id: selectedTestDevice?.id || null,
                message: testMessage,
                slack_webhook_url: testSlackWebhook || null,
            },
        }).then((resp) => {
            const dispatchErrorsCount = resp?.dispatch_errors?.length || 0;
            const dispatchModeLabel = resp?.dispatch_mode === "inline" ? "inline" : "cola";
            const baseMessage = resp?.message || "Notificaciones de prueba encoladas";
            const fullMessage = dispatchErrorsCount > 0
                ? `${baseMessage} (${dispatchModeLabel}, ${dispatchErrorsCount} con error)`
                : `${baseMessage} (${dispatchModeLabel})`;

            setLastDispatchMessage(fullMessage);
            setLastDispatchSeverity(dispatchErrorsCount > 0 ? "warning" : "success");
            setOpenTestModal(false);
            setListLoading(true);
            loadFilterOptions();
        }).catch((error) => {
            const message = error?.response?.data?.error
                || error?.response?.data?.errors?.[0]
                || "No se pudo enviar la notificación de prueba";
            setLastDispatchMessage(message);
            setLastDispatchSeverity("error");
        })
            .finally(() => {
                setSendingTest(false);
            });
    };

    const resendFailedNotification = (notification) => {
        if (!notification?.id) return;

        setResendingNotificationId(notification.id);
        deviceAlertServices.resendNotification(notification.id)
            .then((resp) => {
                setLastDispatchMessage(resp?.message || "Notificación reenviada correctamente");
                setLastDispatchSeverity("success");
                setListLoading(true);
            })
            .catch((error) => {
                const message = error?.response?.data?.error
                    || error?.response?.data?.errors?.[0]
                    || "No se pudo reenviar la notificación";
                setLastDispatchMessage(message);
                setLastDispatchSeverity("error");
            })
            .finally(() => {
                setResendingNotificationId(null);
            });
    };

    const handleSearchQueryChange = (newVal) => {
        setSearchQuery(newVal);
    };

    const debounceSearchQueryChange = useMemo(() => _.debounce(handleSearchQueryChange, 250), []);

    useEffect(() => {
        return () => debounceSearchQueryChange.cancel();
    }, [debounceSearchQueryChange]);

    const filteredRecords = useMemo(() => {
        if (!searchQuery?.trim()) return recordsList;
        const query = searchQuery.trim().toLowerCase();
        return _.filter(recordsList, (record) => {
            const haystack = [
                record?.device_name,
                record?.automation_name,
                record?.device_alert_rule_name,
                record?.event_kind_label,
                record?.user_name,
                record?.notification_group,
                record?.reason,
                record?.channel,
                record?.destination,
            ].join(" ").toLowerCase();
            return haystack.includes(query);
        });
    }, [recordsList, searchQuery]);

    const stats = useMemo(() => {
        return _.countBy(recordsList, "status");
    }, [recordsList]);

    const testUsers = useMemo(() => testOptions?.users || [], [testOptions]);
    const testDevices = useMemo(() => testOptions?.devices || [], [testOptions]);
    const testChannels = useMemo(() => testOptions?.channels || [], [testOptions]);
    const filterChannels = useMemo(() => filterOptions?.channels?.length ? filterOptions.channels : ["email", "whatsapp", "slack"], [filterOptions]);
    const filterStatuses = useMemo(() => filterOptions?.statuses?.length ? filterOptions.statuses : ["sent", "failed", "pending", "skipped"], [filterOptions]);
    const filterAutomations = useMemo(() => filterOptions?.automations || [], [filterOptions]);
    const filterDevices = useMemo(() => filterOptions?.devices || [], [filterOptions]);
    const filterGroups = useMemo(() => filterOptions?.groups || [], [filterOptions]);

    const canSendTestNotifications = selectedTestUsers.length > 0 && selectedTestChannels.length > 0;

    const statusColor = (status) => {
        switch (status) {
            case "sent":
                return "success";
            case "failed":
                return "error";
            case "pending":
                return "warning";
            default:
                return "default";
        }
    };

    const channelColor = (channel) => {
        switch (channel) {
            case "whatsapp":
                return "success";
            case "slack":
                return "primary";
            default:
                return "default";
        }
    };

    const groupLabel = (notification) => notification?.notification_group || UNGROUPED_GROUP_LABEL;

    const openNotificationMenu = (event, notification) => {
        event.stopPropagation();
        setNotificationMenuAnchorEl(event.currentTarget);
        setNotificationMenuRecord(notification);
    };

    const closeNotificationMenu = () => {
        setNotificationMenuAnchorEl(null);
        setNotificationMenuRecord(null);
    };

    const toggleRecordExpanded = (recordId) => {
        setExpandedRecordIds((prev) => ({
            ...prev,
            [recordId]: !prev[recordId],
        }));
    };

    const resetFilters = () => {
        setChannelFilter("");
        setStatusFilter("");
        setSourceFilter("");
        setAutomationFilter("");
        setDeviceFilter("");
        setGroupFilter("");
        setFromDateFilter("");
        setToDateFilter("");
        setSearchInput("");
        setSearchQuery("");
        requestRecordsReload(true);
    };

    const renderMetaChip = ({ icon, label, color = "default" }) => (
        <Chip
            size="small"
            icon={icon}
            label={label}
            color={color}
            variant="outlined"
            sx={{ borderRadius: 999, "& .MuiChip-icon": { fontSize: 16 } }}
        />
    );

    const renderRecordsList = () => {
        if (!filteredRecords.length) {
            return (
                <Paper sx={{ p: 4, borderRadius: 3, textAlign: "center", border: "1px dashed", borderColor: "divider" }}>
                    <Typography variant="h6" mb={1}>No hay notificaciones para mostrar</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Aquí queda todo lo que envían tus automatizaciones: avisos de reglas y avisos de seguridad.
                    </Typography>
                </Paper>
            );
        }

        return (
            <Stack spacing={1.25}>
                {_.map(filteredRecords, (notification) => {
                    const isExpanded = !!expandedRecordIds[notification.id];
                    const createdAt = notification?.created_at ? moment(notification.created_at).format("DD MMM YYYY HH:mm") : "-";
                    const sentAt = notification?.sent_at ? moment(notification.sent_at).format("DD MMM YYYY HH:mm") : null;
                    const metricAt = notification?.device_metric_time ? moment(notification.device_metric_time).format("DD MMM YYYY HH:mm:ss") : null;
                    const isSafety = notification?.source === "safety";
                    const title = notification?.automation_name || (isSafety ? SOURCE_LABELS.safety : "Regla sin nombre");
                    const subject = notification?.device_name || (isSafety ? notification?.event_kind_label : null) || "Sin dispositivo";
                    const recipient = notification?.user_name || notification?.destination;

                    return (
                        <Paper
                            key={`alert-notification-${notification.id}`}
                            variant="outlined"
                            sx={{
                                p: { xs: 1.5, md: 1.75 },
                                borderRadius: 3,
                                borderColor: notification?.status === "failed" ? "error.light" : "rgba(15, 23, 42, 0.08)",
                                background: notification?.status === "failed"
                                    ? "linear-gradient(135deg, rgba(255,255,255,0.98), rgba(255,241,242,0.92))"
                                    : "linear-gradient(135deg, rgba(255,255,255,0.98), rgba(248,250,252,0.92))",
                                boxShadow: "0 12px 34px rgba(15, 23, 42, 0.05)",
                            }}>
                            <Stack spacing={1}>
                                <Stack direction="row" alignItems="center" spacing={1.5}>
                                    <Box
                                        sx={{
                                            width: 42,
                                            height: 42,
                                            borderRadius: 2.5,
                                            display: "grid",
                                            placeItems: "center",
                                            color: notification?.status === "failed" ? "error.main" : "primary.main",
                                            bgcolor: notification?.status === "failed" ? "rgba(244, 67, 54, 0.08)" : "rgba(103, 58, 183, 0.08)",
                                            flex: "0 0 auto",
                                        }}>
                                        {isSafety ? <ShieldOutlinedIcon fontSize="small" /> : <NotificationsActiveOutlinedIcon fontSize="small" />}
                                    </Box>

                                    <Stack spacing={0.35} sx={{ minWidth: 0, flex: 1 }}>
                                        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ flexWrap: "wrap", minWidth: 0 }}>
                                            <Typography variant="h6" noWrap sx={{ minWidth: 0, lineHeight: 1.15 }}>
                                                {title}
                                            </Typography>
                                            <Chip
                                                size="small"
                                                variant="outlined"
                                                color={isSafety ? "error" : "default"}
                                                label={isSafety && notification?.event_kind_label ? notification.event_kind_label : SOURCE_LABELS[notification?.source] || SOURCE_LABELS.rule}
                                                sx={{ borderRadius: 999 }}
                                            />
                                            <Chip
                                                size="small"
                                                label={STATUS_LABELS[notification?.status] || notification?.status}
                                                color={statusColor(notification?.status)}
                                                sx={{ borderRadius: 999 }}
                                            />
                                        </Stack>
                                        <Typography variant="body2" color="text.secondary" noWrap>
                                            {subject} · {groupLabel(notification)} · Registrada {createdAt}
                                        </Typography>
                                    </Stack>

                                    <Stack direction="row" spacing={0.75} alignItems="center" sx={{ display: { xs: "none", lg: "flex" } }}>
                                        {renderMetaChip({ icon: <SendOutlinedIcon />, label: CHANNEL_LABELS[notification?.channel] || notification?.channel, color: channelColor(notification?.channel) })}
                                        {!!recipient && renderMetaChip({ icon: <PersonOutlineOutlinedIcon />, label: recipient })}
                                        {renderMetaChip({ icon: <AccessTimeOutlinedIcon />, label: sentAt ? `Enviada ${sentAt}` : "Sin envío" })}
                                    </Stack>

                                    <Stack direction="row" spacing={0.25} alignItems="center" flex="0 0 auto">
                                        {resendingNotificationId === notification.id && <CircularProgress size={16} />}
                                        <Tooltip title={isExpanded ? "Ocultar detalle" : "Ver detalle"}>
                                            <IconButton size="small" onClick={() => toggleRecordExpanded(notification.id)}>
                                                {isExpanded ? <ExpandLessOutlinedIcon fontSize="small" /> : <ExpandMoreOutlinedIcon fontSize="small" />}
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Acciones">
                                            <IconButton
                                                size="small"
                                                disabled={resendingNotificationId === notification.id}
                                                onClick={(event) => openNotificationMenu(event, notification)}>
                                                <MoreVertOutlinedIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </Stack>
                                </Stack>

                                <Stack direction="row" spacing={0.75} useFlexGap sx={{ display: { xs: "flex", lg: "none" }, flexWrap: "wrap" }}>
                                    {renderMetaChip({ icon: <SendOutlinedIcon />, label: CHANNEL_LABELS[notification?.channel] || notification?.channel, color: channelColor(notification?.channel) })}
                                    {!!recipient && renderMetaChip({ icon: <PersonOutlineOutlinedIcon />, label: recipient })}
                                </Stack>

                                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                    <Grid container spacing={1.25} sx={{ pt: 1 }}>
                                        <Grid item xs={12} md={4}>
                                            <Typography variant="caption" color="text.secondary">Destino</Typography>
                                            <Typography variant="body2">{notification?.destination || "-"}</Typography>
                                        </Grid>
                                        <Grid item xs={12} md={4}>
                                            <Typography variant="caption" color="text.secondary">{isSafety ? "Evento" : "Métrica evaluada"}</Typography>
                                            <Typography variant="body2">{isSafety ? notification?.event_kind_label || "-" : metricAt || "-"}</Typography>
                                        </Grid>
                                        <Grid item xs={12} md={4}>
                                            <Typography variant="caption" color="text.secondary">Fecha de envío</Typography>
                                            <Typography variant="body2">{sentAt || "-"}</Typography>
                                        </Grid>
                                        {!!notification?.reason && (
                                            <Grid item xs={12}>
                                                <Alert severity={isSafety ? "warning" : "info"} icon={isSafety ? <ShieldOutlinedIcon fontSize="small" /> : <RuleOutlinedIcon fontSize="small" />}>
                                                    {notification.reason}
                                                </Alert>
                                            </Grid>
                                        )}
                                        {!!notification?.error_message && (
                                            <Grid item xs={12}>
                                                <Alert severity="error">
                                                    {notification.error_message}
                                                </Alert>
                                            </Grid>
                                        )}
                                    </Grid>
                                </Collapse>
                            </Stack>
                        </Paper>
                    );
                })}
            </Stack>
        );
    };

    const renderTestNotificationsModal = () => {
        if (!isSuperadmin) return null;

        return (
            <Dialog
                fullWidth
                maxWidth="md"
                open={openTestModal}
                onClose={closeTestNotificationsModal}>
                <DialogTitle>
                    <Typography variant="h4">Enviar notificación de prueba</Typography>
                </DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ pt: 1 }}>
                        <Alert severity="info">
                            Esta función crea notificaciones reales y las envía por los canales seleccionados.
                        </Alert>

                        {!!testOptions?.users_limited && (
                            <Alert severity="warning">
                                Se cargó un subconjunto de usuarios para mantener el rendimiento. Ajusta backend si necesitas más.
                            </Alert>
                        )}

                        {!!testOptions?.devices_limited && (
                            <Alert severity="warning">
                                Se cargó un subconjunto de dispositivos para esta prueba.
                            </Alert>
                        )}

                        <Autocomplete
                            multiple
                            options={testUsers}
                            loading={testOptionsLoading}
                            value={selectedTestUsers}
                            onChange={(_e, value) => setSelectedTestUsers(value)}
                            getOptionLabel={(option) => option?.full_name || option?.email || `Usuario ${option?.id}`}
                            isOptionEqualToValue={(option, value) => option?.id === value?.id}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Usuarios destino"
                                    placeholder="Selecciona usuarios"
                                    helperText="Puedes seleccionar uno o más usuarios."
                                />
                            )}
                        />

                        <Autocomplete
                            multiple
                            options={testChannels}
                            loading={testOptionsLoading}
                            value={selectedTestChannels}
                            onChange={(_e, value) => setSelectedTestChannels(value)}
                            getOptionLabel={(option) => CHANNEL_LABELS[option] || option}
                            isOptionEqualToValue={(option, value) => option === value}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Canales"
                                    placeholder="Selecciona canales"
                                    helperText="Email y WhatsApp usan los datos del usuario; Slack puede usar webhook global o uno custom."
                                />
                            )}
                        />

                        <Autocomplete
                            options={testDevices}
                            loading={testOptionsLoading}
                            value={selectedTestDevice}
                            onChange={(_e, value) => setSelectedTestDevice(value)}
                            getOptionLabel={(option) => _.compact([
                                option?.name,
                                option?.store_name ? `(${option.store_name})` : "",
                                option?.zone ? `- ${option.zone}` : "",
                            ]).join(" ")}
                            isOptionEqualToValue={(option, value) => option?.id === value?.id}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Dispositivo (opcional)"
                                    helperText="Si no eliges uno, backend usa el primer dispositivo de la empresa."
                                />
                            )}
                        />

                        {selectedTestChannels.includes("slack") && (
                            <TextField
                                fullWidth
                                label="Webhook Slack (opcional)"
                                value={testSlackWebhook}
                                onChange={(e) => setTestSlackWebhook(e.target.value)}
                                helperText="Si lo dejas vacío, se usará el webhook global configurado en backend."
                            />
                        )}

                        <TextField
                            fullWidth
                            multiline
                            minRows={2}
                            label="Mensaje de prueba"
                            value={testMessage}
                            onChange={(e) => setTestMessage(e.target.value)}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeTestNotificationsModal}>Cerrar</Button>
                    <LoadingButton
                        loading={sendingTest}
                        variant="contained"
                        color="warning"
                        disabled={!canSendTestNotifications}
                        onClick={sendTestNotifications}
                        endIcon={<SendOutlinedIcon />}
                        loadingPosition="end">
                        {sendingTest ? "Enviando" : "Enviar prueba"}
                    </LoadingButton>
                </DialogActions>
            </Dialog>
        );
    };

    return (
        <div>
            {renderTestNotificationsModal()}
            <StandardPageHeader
                title="Notificaciones"
                subtitle={`Todo lo que enviaron las automatizaciones: avisos de reglas y avisos de seguridad (${totalCount} registros).`}
            />

            <StandardFiltersBar sx={{ borderRadius: 3 }}>
                <Grid container spacing={1.25} alignItems="center">
                    {isSuperadmin && (
                        <Grid item xs={12} sm={6} md={2}>
                            <CreateButton
                                fullWidth
                                startIcon={<SendOutlinedIcon />}
                                sx={{ whiteSpace: "nowrap" }}
                                onClick={openTestNotificationsModal}>
                                Enviar prueba
                            </CreateButton>
                        </Grid>
                    )}

                    <Grid item xs={12} sm={6} md={2}>
                        <TextField
                            select
                            size="small"
                            fullWidth
                            label="Canal"
                            value={channelFilter}
                            onChange={(e) => {
                                setChannelFilter(e.target.value);
                                requestRecordsReload(true);
                            }}>
                            <MenuItem value="">Todos</MenuItem>
                            {_.map(filterChannels, (channel) => (
                                <MenuItem key={`channel-filter-${channel}`} value={channel}>
                                    {CHANNEL_LABELS[channel] || channel}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>

                    <Grid item xs={12} sm={6} md={2}>
                        <TextField
                            select
                            size="small"
                            fullWidth
                            label="Estado"
                            value={statusFilter}
                            onChange={(e) => {
                                setStatusFilter(e.target.value);
                                requestRecordsReload(true);
                            }}>
                            <MenuItem value="">Todos</MenuItem>
                            {_.map(filterStatuses, (status) => (
                                <MenuItem key={`status-filter-${status}`} value={status}>
                                    {STATUS_LABELS[status] || status}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>

                    <Grid item xs={12} sm={6} md={2}>
                        <TextField
                            select
                            size="small"
                            fullWidth
                            label="Grupo"
                            value={groupFilter}
                            onChange={(e) => {
                                setGroupFilter(e.target.value);
                                requestRecordsReload(true);
                            }}>
                            <MenuItem value="">Todos</MenuItem>
                            <MenuItem value={UNGROUPED_GROUP_VALUE}>{UNGROUPED_GROUP_LABEL}</MenuItem>
                            {_.map(filterGroups, (group) => (
                                <MenuItem key={`group-filter-${group}`} value={group}>
                                    {group}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>

                    <Grid item xs={12} sm={6} md={2}>
                        <TextField
                            select
                            size="small"
                            fullWidth
                            label="Origen"
                            value={sourceFilter}
                            onChange={(e) => {
                                setSourceFilter(e.target.value);
                                requestRecordsReload(true);
                            }}>
                            <MenuItem value="">Todos</MenuItem>
                            {_.map(SOURCE_LABELS, (label, value) => (
                                <MenuItem key={`source-filter-${value}`} value={value}>{label}</MenuItem>
                            ))}
                        </TextField>
                    </Grid>

                    <Grid item xs={12} sm={6} md={2}>
                        <TextField
                            select
                            size="small"
                            fullWidth
                            label="Automatización"
                            value={automationFilter}
                            onChange={(e) => {
                                setAutomationFilter(e.target.value);
                                requestRecordsReload(true);
                            }}>
                            <MenuItem value="">Todas</MenuItem>
                            {_.map(filterAutomations, (automation) => (
                                <MenuItem key={`automation-filter-${automation.key}`} value={automation.key}>
                                    {automation.name}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>

                    <Grid item xs={12} sm={6} md={2}>
                        <TextField
                            select
                            size="small"
                            fullWidth
                            label="Dispositivo"
                            value={deviceFilter}
                            onChange={(e) => {
                                setDeviceFilter(e.target.value);
                                requestRecordsReload(true);
                            }}>
                            <MenuItem value="">Todos</MenuItem>
                            {_.map(filterDevices, (device) => (
                                <MenuItem key={`device-filter-${device.id}`} value={device.id}>
                                    {device.name}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>

                    <Grid item xs={12} sm={6} md={2}>
                        <TextField
                            fullWidth
                            size="small"
                            type="date"
                            label="Desde"
                            InputLabelProps={{ shrink: true }}
                            value={fromDateFilter}
                            onChange={(e) => {
                                setFromDateFilter(e.target.value);
                                requestRecordsReload(true);
                            }}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6} md={2}>
                        <TextField
                            fullWidth
                            size="small"
                            type="date"
                            label="Hasta"
                            InputLabelProps={{ shrink: true }}
                            value={toDateFilter}
                            onChange={(e) => {
                                setToDateFilter(e.target.value);
                                requestRecordsReload(true);
                            }}
                        />
                    </Grid>

                    <Grid item xs={12} md={isSuperadmin ? 6 : 8}>
                        <StandardSearchField
                            fullWidth
                            value={searchInput}
                            placeholder="Buscar en los resultados cargados"
                            onChange={(e) => {
                                setSearchInput(e.target.value);
                                debounceSearchQueryChange(e.target.value);
                            }}
                            onEnter={(value) => {
                                setSearchInput(value);
                                handleSearchQueryChange(value);
                            }}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6} md={2}>
                        <Button
                            fullWidth
                            size="small"
                            variant="outlined"
                            startIcon={<TuneOutlinedIcon />}
                            disabled={filterOptionsLoading}
                            onClick={resetFilters}
                            sx={{ minHeight: 42 }}>
                            Limpiar
                        </Button>
                    </Grid>
                </Grid>
            </StandardFiltersBar>

            {!!lastDispatchMessage && (
                <Alert severity={lastDispatchSeverity} sx={{ mb: 2 }}>
                    {lastDispatchMessage}
                </Alert>
            )}

            <Stack direction="row" spacing={1} mb={2} useFlexGap sx={{ flexWrap: "wrap" }}>
                <Chip size="small" label={`Enviadas: ${stats.sent || 0}`} color="success" />
                <Chip size="small" label={`Fallidas: ${stats.failed || 0}`} color="error" />
                <Chip size="small" label={`Pendientes: ${stats.pending || 0}`} color="warning" />
                <Chip size="small" label={`Omitidas: ${stats.skipped || 0}`} />
                {filterOptionsLoading && <Chip size="small" label="Cargando filtros" icon={<CircularProgress size={12} />} />}
            </Stack>

            <Div sx={{ display: listLoading ? "block" : "none", textAlign: "center", my: 5 }}>
                <CircularProgress />
            </Div>

            {!listLoading && renderRecordsList()}

            <CustomPagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageSelect={(page) => {
                    setCurrentPage(page);
                    setListLoading(true);
                }}
            />

            <Menu
                anchorEl={notificationMenuAnchorEl}
                open={Boolean(notificationMenuAnchorEl)}
                onClose={closeNotificationMenu}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}>
                <MenuItem
                    onClick={() => {
                        const selectedRecord = notificationMenuRecord;
                        closeNotificationMenu();
                        toggleRecordExpanded(selectedRecord?.id);
                    }}>
                    {expandedRecordIds[notificationMenuRecord?.id] ? (
                        <ExpandLessOutlinedIcon fontSize="small" sx={{ mr: 1 }} />
                    ) : (
                        <ExpandMoreOutlinedIcon fontSize="small" sx={{ mr: 1 }} />
                    )}
                    {expandedRecordIds[notificationMenuRecord?.id] ? "Ocultar detalle" : "Ver detalle"}
                </MenuItem>
                {notificationMenuRecord?.status === "failed" && (
                    <MenuItem
                        onClick={() => {
                            const selectedRecord = notificationMenuRecord;
                            closeNotificationMenu();
                            resendFailedNotification(selectedRecord);
                        }}>
                        <ReplayOutlinedIcon fontSize="small" sx={{ mr: 1 }} />
                        Reenviar
                    </MenuItem>
                )}
            </Menu>
        </div>
    );
};

export default DeviceAlertNotificationsIndex;
